import React, { memo, useEffect, useMemo, useState } from 'react';
import {
  TextField,
  MenuItem,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { Add } from '@mui/icons-material';
import { map } from 'lodash';
import { combineDateTimeInZone as combineDateTime } from '../../utils/showingDateTime';

interface DateTimeRow {
  id: number;
  date: Date | null;
  time: string;
}

/**
 * Formats a date into a time string with 15-minute intervals
 * @param date The date to format
 * @returns A time string in the format "h:mm AM/PM" within the available time range
 */
const getTimeString = (date: Date): string => {
  // Round to nearest 15 minutes
  const minutes = date.getMinutes();
  const roundedMinutes = Math.round(minutes / 15) * 15;

  // Create a new date with the rounded minutes
  const roundedDate = new Date(date);
  roundedDate.setMinutes(roundedMinutes % 60);
  // If rounding pushed us to the next hour
  if (roundedMinutes === 60) {
    roundedDate.setHours(roundedDate.getHours() + 1);
  }

  const formattedTime = roundedDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  // Check if the time is in the available range
  const availableTimes = [
    '7:00 AM',
    '7:15 AM',
    '7:30 AM',
    '7:45 AM',
    '8:00 AM',
    '8:15 AM',
    '8:30 AM',
    '8:45 AM',
    '9:00 AM',
    '9:15 AM',
    '9:30 AM',
    '9:45 AM',
    '10:00 AM',
    '10:15 AM',
    '10:30 AM',
    '10:45 AM',
    '11:00 AM',
    '11:15 AM',
    '11:30 AM',
    '11:45 AM',
    '12:00 PM',
    '12:15 PM',
    '12:30 PM',
    '12:45 PM',
    '1:00 PM',
    '1:15 PM',
    '1:30 PM',
    '1:45 PM',
    '2:00 PM',
    '2:15 PM',
    '2:30 PM',
    '2:45 PM',
    '3:00 PM',
    '3:15 PM',
    '3:30 PM',
    '3:45 PM',
    '4:00 PM',
    '4:15 PM',
    '4:30 PM',
    '4:45 PM',
    '5:00 PM',
    '5:15 PM',
    '5:30 PM',
    '5:45 PM',
    '6:00 PM',
    '6:15 PM',
    '6:30 PM',
    '6:45 PM',
    '7:00 PM',
  ];

  if (availableTimes.includes(formattedTime)) {
    return formattedTime;
  }

  // If time is outside range, return the closest available time
  const hour = roundedDate.getHours();

  if (hour < 7) {
    return '7:00 AM'; // Before start of range
  } else if (hour > 19 || (hour === 19 && roundedDate.getMinutes() > 0)) {
    return '7:00 PM'; // After end of range
  }

  // This should not happen given the checks above, but as a fallback
  return formattedTime;
};

interface Props {
  multiple?: boolean;
  handleTimesSelected: (times: Date[]) => void;
  minDate?: Date;
  label?: string | boolean;
  timeOnly?: boolean;
  disabledTimes?: Date[];
  /**
   * IANA timezone of the listing. When set, the wall-clock the user picks
   * is interpreted in this zone rather than the browser's, so cross-TZ
   * bookings store the correct UTC instant for the property's local time.
   */
  timezone?: string;
}

const TimeSelection: React.FC<Props> = ({
  handleTimesSelected,
  multiple = false,
  minDate,
  label,
  timeOnly = false,
  disabledTimes = [],
  timezone,
}) => {
  const getInitialDate = () => {
    const now = new Date();
    if (minDate && minDate > now) {
      return minDate;
    }

    // Round up to the next 15-minute interval
    const minutes = now.getMinutes();
    const minutesToAdd = 15 - (minutes % 15);
    if (minutesToAdd < 15) {
      now.setMinutes(now.getMinutes() + minutesToAdd);
    }
    now.setSeconds(0);
    now.setMilliseconds(0);

    return now;
  };

  // Add this function to check if a time is disabled
  const isTimeDisabled = (date: Date | null, timeStr: string): boolean => {
    if (!date || !disabledTimes || disabledTimes.length === 0) return false;

    const dateToCheck = timeOnly
      ? combineDateTime(new Date(), timeStr, timezone)
      : combineDateTime(date, timeStr, timezone);

    return disabledTimes.some((disabledTime) => {
      // Get the timestamp for the disabled time
      const disabledTimestamp = disabledTime.getTime();

      // Get the timestamp for the time we're checking
      const checkTimestamp = dateToCheck.getTime();

      // Calculate one hour in milliseconds
      const oneHour = 60 * 60 * 1000;
      const fortyFiveMinutes = 45 * 60 * 1000;

      // Check if the time we're checking falls within one hour after the disabled time
      // OR one hour before the disabled time
      return (
        (checkTimestamp >= disabledTimestamp &&
          checkTimestamp < disabledTimestamp + oneHour) ||
        (checkTimestamp >= disabledTimestamp - fortyFiveMinutes &&
          checkTimestamp < disabledTimestamp)
      );
    });
  };

  // Get a valid initial time that's not disabled
  const getInitialValidTime = () => {
    const initialDate = getInitialDate();
    const initialTimeString = getTimeString(initialDate);

    // If the initial time is not disabled, use it
    if (!isTimeDisabled(initialDate, initialTimeString)) {
      return { date: initialDate, time: initialTimeString };
    }

    // Otherwise, find the first available time
    const times = [
      '7:00 AM',
      '7:15 AM',
      '7:30 AM',
      '7:45 AM',
      '8:00 AM',
      // ... all other times
      '7:00 PM',
    ];

    const firstAvailableTime = times.find(
      (time) => !isTimeDisabled(initialDate, time)
    );

    return {
      date: initialDate,
      time: firstAvailableTime || initialTimeString, // Fallback to initial time if somehow all times are disabled
    };
  };

  const [rows, setRows] = useState<DateTimeRow[]>(() => {
    const initialValid = getInitialValidTime();
    return [
      {
        id: 1,
        date: initialValid.date,
        time: initialValid.time,
      },
    ];
  });

  const times = useMemo(
    () => [
      '7:00 AM',
      '7:15 AM',
      '7:30 AM',
      '7:45 AM',
      '8:00 AM',
      '8:15 AM',
      '8:30 AM',
      '8:45 AM',
      '9:00 AM',
      '9:15 AM',
      '9:30 AM',
      '9:45 AM',
      '10:00 AM',
      '10:15 AM',
      '10:30 AM',
      '10:45 AM',
      '11:00 AM',
      '11:15 AM',
      '11:30 AM',
      '11:45 AM',
      '12:00 PM',
      '12:15 PM',
      '12:30 PM',
      '12:45 PM',
      '1:00 PM',
      '1:15 PM',
      '1:30 PM',
      '1:45 PM',
      '2:00 PM',
      '2:15 PM',
      '2:30 PM',
      '2:45 PM',
      '3:00 PM',
      '3:15 PM',
      '3:30 PM',
      '3:45 PM',
      '4:00 PM',
      '4:15 PM',
      '4:30 PM',
      '4:45 PM',
      '5:00 PM',
      '5:15 PM',
      '5:30 PM',
      '5:45 PM',
      '6:00 PM',
      '6:15 PM',
      '6:30 PM',
      '6:45 PM',
      '7:00 PM',
    ],
    []
  );

  const handleDateChange = (id: number, newDate: Date | null) => {
    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id === id) {
          // Check if the current time is disabled for the new date
          const isCurrentTimeDisabled = isTimeDisabled(newDate, row.time);

          // If the current time is disabled for the new date, find the first available time
          if (isCurrentTimeDisabled) {
            const firstAvailableTime = times.find(
              (time) => !isTimeDisabled(newDate, time)
            );
            return {
              ...row,
              date: newDate,
              time: firstAvailableTime || row.time,
            };
          }

          return { ...row, date: newDate };
        }
        return row;
      })
    );
  };

  const handleTimeChange = (id: number, newTime: string) => {
    setRows((prevRows) =>
      prevRows.map((row) => (row.id === id ? { ...row, time: newTime } : row))
    );
  };

  const addRow = () => {
    if (rows.length >= 3) {
      return;
    }

    const initialValid = getInitialValidTime();
    setRows((prevRows) => [
      ...prevRows,
      {
        id: prevRows.length + 1,
        date: initialValid.date,
        time: initialValid.time,
      },
    ]);
  };

  useEffect(() => {
    const combinedDateTimes = map(rows, (item) => {
      if (timeOnly) {
        const today = new Date();
        return combineDateTime(today, item.time, timezone);
      }
      return combineDateTime(item.date!, item.time, timezone);
    });
    handleTimesSelected(combinedDateTimes);
  }, [rows, timeOnly, timezone]);

  return (
    <Grid container item spacing={2}>
      <Grid item xs={12}>
        <Stack direction="row" alignItems={'center'} spacing={0}>
          {label !== false && (
            <Typography fontWeight={500}>
              {label || `Choose tour ${multiple ? 'times' : 'time'}`}
            </Typography>
          )}
          {multiple && (
            <>
              <IconButton onClick={addRow} disabled={rows.length === 3}>
                <Add />
              </IconButton>
            </>
          )}
        </Stack>
        {multiple && (
          <Typography variant="caption">
            You can choose up to three times that work for you.
          </Typography>
        )}
      </Grid>

      {rows.map((row) => (
        <Grid container item spacing={2} key={row.id}>
          {!timeOnly && (
            <Grid item xs={timeOnly ? 12 : 6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Select Date"
                  value={row.date}
                  onChange={(newDate) => handleDateChange(row.id, newDate)}
                  minDate={
                    minDate && minDate > new Date() ? minDate : new Date()
                  }
                  sx={{ width: '100%' }}
                />
              </LocalizationProvider>
            </Grid>
          )}
          <Grid item xs={timeOnly ? 12 : 6}>
            <TextField
              select
              label="Select Time"
              value={row.time}
              onChange={(e) => handleTimeChange(row.id, e.target.value)}
              fullWidth
            >
              {times.map((time) => (
                <MenuItem
                  key={time}
                  value={time}
                  disabled={isTimeDisabled(row.date, time)}
                >
                  {time}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
};

export default memo(TimeSelection);
